import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { getCurrentUser, login, resendLoginOtp, verifyLoginOtp } from '@/lib/apiService';
import { resolvePostLoginRedirect } from '@/lib/accessControl';
import { getGoogleOAuthUrl } from '@/lib/oauth/google';
import { AuthResponse } from '@/types/auth';
import {
  CONSENT_REQUIRED_ROUTE,
  clearConsentGateState,
  persistConsentGateState,
} from '@/lib/consentGate';
import {
  clearSubscriptionGateState,
  isSubscriptionRequiredApiError,
  readSubscriptionGateState,
  syncSubscriptionGateFromAuthStatus,
} from '@/lib/subscriptionGate';
import { extractErrorMessage, showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { applyOtpInputValue, applyOtpPasteValue, isOtpSubmitKey } from '@/utils/otp-input';
import nutritionistSignupVisual from '@/assets/images/appointments/nutritionist-signup.svg';
import nutritionOnboarding from '@/assets/images/nutrition_onboarding.svg';
import menuBarLogo from '@/assets/images/menu-bar-logo.svg';
import googleOAuthIcon from '@/assets/images/auth/google-oauth.svg';

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type LoginLocationState = {
  from?: string;
};

const LOGIN_MFA_EMAIL_STORAGE_KEY = 'caloriescience_login_mfa_email';
const OTP_LENGTH = 6;
const UNVERIFIED_EMAIL_MESSAGE = 'please verify your email before logging in';

const readRetryAfterSeconds = (error: unknown): number => {
  if (!(error instanceof AxiosError)) {
    return 0;
  }
  const rawRetryAfter = error.response?.data?.retryAfterSeconds;
  const retryAfterSeconds = Number(rawRetryAfter);
  if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
    return 0;
  }
  return Math.ceil(retryAfterSeconds);
};

const formatRetryAfter = (retryAfterSeconds: number): string => {
  if (retryAfterSeconds < 60) {
    return `${retryAfterSeconds} seconds`;
  }

  const totalMinutes = Math.ceil(retryAfterSeconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes} minute${totalMinutes === 1 ? '' : 's'}`;
  }

  const totalHours = Math.ceil(totalMinutes / 60);
  return `${totalHours} hour${totalHours === 1 ? '' : 's'}`;
};

const normalizeLoginEmail = (value: string): string => value.trim().toLowerCase();

const isUnverifiedEmailLoginError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError) || error.response?.status !== 401) {
    return false;
  }

  return extractErrorMessage(error).toLowerCase().includes(UNVERIFIED_EMAIL_MESSAGE);
};

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'credentials' | 'otp'>(() => {
    const savedEmail = sessionStorage.getItem(LOGIN_MFA_EMAIL_STORAGE_KEY)?.trim() ?? '';
    return savedEmail ? 'otp' : 'credentials';
  });
  const [pendingEmail, setPendingEmail] = useState(() => sessionStorage.getItem(LOGIN_MFA_EMAIL_STORAGE_KEY)?.trim() ?? '');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const intendedReturnPath =
    (location.state as LoginLocationState | null)?.from || searchParams.get('from') || null;

  useEffect(() => {
    if (pendingEmail) {
      sessionStorage.setItem(LOGIN_MFA_EMAIL_STORAGE_KEY, pendingEmail);
      return;
    }
    sessionStorage.removeItem(LOGIN_MFA_EMAIL_STORAGE_KEY);
  }, [pendingEmail]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setResendCooldownSeconds((previous) => Math.max(previous - 1, 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [resendCooldownSeconds]);

  const handleGoogleOAuth = useCallback(() => {
    const googleOAuthUrl = getGoogleOAuthUrl();
    if (!googleOAuthUrl) {
      showWarningToast(
        'Google sign-in not configured',
        'Please set VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_REDIRECT_URI.'
      );
      return;
    }

    window.location.href = googleOAuthUrl;
  }, []);

  const resetOtpState = useCallback(() => {
    setOtp(Array(OTP_LENGTH).fill(''));
  }, []);

  const returnToCredentialsStep = useCallback(() => {
    setStep('credentials');
    setPendingEmail('');
    setResendCooldownSeconds(0);
    resetOtpState();
  }, [resetOtpState]);

  const resolveLoginRedirect = useCallback(
    (user: AuthResponse['user'] | null | undefined) =>
      resolvePostLoginRedirect(intendedReturnPath, user ?? getCurrentUser()),
    [intendedReturnPath],
  );

  const handleConsentBlocked = useCallback((response: AuthResponse): boolean => {
    const consentStatus = response.consent;
    const returnTo = resolveLoginRedirect(response.user);
    if (!consentStatus?.accessBlocked) {
      clearConsentGateState();
      return false;
    }

    persistConsentGateState({
      accessBlocked: true,
      reason: consentStatus.reason ?? null,
      returnTo,
    });
    returnToCredentialsStep();
    showWarningToast(
      'Consent required',
      'Please review and accept terms and privacy consent to continue.'
    );
    navigate(`${CONSENT_REQUIRED_ROUTE}?from=${encodeURIComponent(returnTo)}`);
    return true;
  }, [navigate, resolveLoginRedirect, returnToCredentialsStep]);

  const handleSubscriptionBlocked = useCallback((response: {
    subscription?: AuthResponse['subscription'];
    user?: AuthResponse['user'];
  }): boolean => {
    const subscriptionStatus = response.subscription;
    if (!subscriptionStatus?.accessBlocked) {
      clearSubscriptionGateState();
      return false;
    }

    const returnTo = resolveLoginRedirect(response.user);
    syncSubscriptionGateFromAuthStatus(subscriptionStatus, returnTo);
    returnToCredentialsStep();
    showWarningToast(
      'Subscription required',
      subscriptionStatus.message || 'Please choose a subscription plan to continue.'
    );
    return true;
  }, [resolveLoginRedirect, returnToCredentialsStep]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    }
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      const response = await login({ email: data.email, password: data.password });

      if (handleSubscriptionBlocked(response)) {
        return;
      }

      if ('requiresMfa' in response && response.requiresMfa) {
        const responseEmail = response.mfa?.email?.trim() || data.email.trim().toLowerCase();
        setPendingEmail(responseEmail);
        setResendCooldownSeconds(Math.max(0, Number(response.mfa?.resendAvailableInSeconds ?? 0)));
        resetOtpState();
        setStep('otp');
        showSuccessToast('OTP sent', 'Enter the 6-digit code sent to your email to continue.');
        return;
      }

      if ('token' in response && response.token && handleConsentBlocked(response)) {
        return;
      }

      if ('token' in response && response.token && handleSubscriptionBlocked(response)) {
        return;
      }

      showSuccessToast('Login successful', 'Welcome back to Calorie Science.');
      navigate(resolveLoginRedirect(response.user ?? getCurrentUser()));
    } catch (error) {
      if (isUnverifiedEmailLoginError(error)) {
        const verificationEmail = normalizeLoginEmail(data.email);
        const params = new URLSearchParams({
          verifyEmail: verificationEmail,
          resend: '1',
        });
        showWarningToast(
          'Email verification required',
          'Enter the code sent to your email to finish setting up your account.'
        );
        navigate(`/signup?${params.toString()}`, {
          replace: true,
          state: {
            autoResendVerification: true,
            verificationEmail,
          },
        });
        return;
      }

      const retryAfterSeconds = readRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        showWarningToast('Please wait', `Try again in ${formatRetryAfter(retryAfterSeconds)}.`);
        return;
      }

      if (isSubscriptionRequiredApiError(error)) {
        returnToCredentialsStep();
        const gateState = readSubscriptionGateState();
        showWarningToast(
          'Subscription required',
          gateState?.message || 'Please choose a subscription plan to continue.',
        );
        return;
      }

      showErrorToast(error, "Login failed", { duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = useCallback((index: number, value: string) => {
    setOtp((previous) => {
      const { next, focusIndex } = applyOtpInputValue(previous, index, value, OTP_LENGTH);
      window.setTimeout(() => {
        otpRefs.current[focusIndex]?.focus();
        otpRefs.current[focusIndex]?.select?.();
      }, 0);
      return next;
    });
  }, []);

  const handleOtpKeyDown = useCallback((index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isOtpSubmitKey(event.key)) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
      return;
    }
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  }, [otp]);

  const handleOtpPaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const nextValues = applyOtpPasteValue(event.clipboardData.getData('text'), OTP_LENGTH);
    if (!nextValues) return;

    setOtp(nextValues);
    const filledCount = nextValues.filter(Boolean).length;
    const nextFocusIndex = Math.min(Math.max(filledCount, 1), OTP_LENGTH) - 1;
    otpRefs.current[nextFocusIndex]?.focus();
  }, []);

  const handleVerifyOtp = useCallback(async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length < OTP_LENGTH) {
      showWarningToast('Incomplete OTP', 'Please enter the 6-digit code.');
      return;
    }

    if (!pendingEmail) {
      showWarningToast('Session expired', 'Please login with email and password again.');
      returnToCredentialsStep();
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await verifyLoginOtp({ email: pendingEmail, otp: enteredOtp });
      if (handleConsentBlocked(response)) {
        return;
      }
      if (handleSubscriptionBlocked(response)) {
        return;
      }
      showSuccessToast('Login successful', 'Welcome back to Calorie Science.');
      returnToCredentialsStep();
      navigate(resolveLoginRedirect(response.user ?? getCurrentUser()));
    } catch (error) {
      const retryAfterSeconds = readRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        setResendCooldownSeconds((previous) => Math.max(previous, retryAfterSeconds));
        showWarningToast('Too many attempts', `Try again in ${formatRetryAfter(retryAfterSeconds)}.`);
        return;
      }
      showErrorToast(error, 'OTP verification failed', { duration: 5000 });
    } finally {
      setIsVerifyingOtp(false);
    }
  }, [handleConsentBlocked, handleSubscriptionBlocked, navigate, otp, pendingEmail, resolveLoginRedirect, returnToCredentialsStep]);

  const handleResendOtp = useCallback(async () => {
    if (resendCooldownSeconds > 0) {
      return;
    }
    if (!pendingEmail) {
      showWarningToast('Session expired', 'Please login with email and password again.');
      returnToCredentialsStep();
      return;
    }

    setIsResendingOtp(true);
    try {
      const response = await resendLoginOtp({ email: pendingEmail });
      const responseRecord = response as Record<string, unknown>;
      const mfaInfo =
        responseRecord.mfa && typeof responseRecord.mfa === 'object'
          ? (responseRecord.mfa as Record<string, unknown>)
          : null;
      const cooldown = Number(mfaInfo?.resendAvailableInSeconds ?? 0);
      if (Number.isFinite(cooldown) && cooldown > 0) {
        setResendCooldownSeconds(Math.ceil(cooldown));
      }
      showSuccessToast('OTP resent', 'Please check your inbox for the latest code.');
      resetOtpState();
    } catch (error) {
      const retryAfterSeconds = readRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        setResendCooldownSeconds((previous) => Math.max(previous, retryAfterSeconds));
        showWarningToast('Please wait', `Try again in ${formatRetryAfter(retryAfterSeconds)}.`);
        return;
      }
      showErrorToast(error, 'Could not resend OTP', { duration: 5000 });
    } finally {
      setIsResendingOtp(false);
    }
  }, [pendingEmail, resendCooldownSeconds, resetOtpState, returnToCredentialsStep]);

  return (
    <div className="h-dvh overflow-hidden bg-[#e9edf1] p-3 sm:p-5 lg:p-6">
      <div className="grid h-full min-h-0 w-full grid-cols-1 grid-rows-1 overflow-hidden rounded-2xl border border-slate-200 lg:grid-cols-2">
        <aside className="relative hidden min-h-0 overflow-y-auto overscroll-contain [scrollbar-gutter:stable] lg:block">
          <div className="relative min-h-[900px] 2xl:min-h-[960px]">
            <img
              src={nutritionistSignupVisual}
              alt="Scale your nutrition practice with intelligent meal planning"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="pointer-events-none relative flex min-h-[900px] flex-col justify-between p-8 2xl:min-h-[960px] xl:p-12">
              <h2 className="max-w-[560px] pt-4 text-[clamp(2.4rem,4vw,4.8rem)] font-medium leading-[0.98] text-white">
                <span>Scale your nutrition</span>
                <br />
                <span>practice 3X</span>
              </h2>

              <div className="flex flex-1 items-center justify-center">
                <img src={nutritionOnboarding} alt="" aria-hidden="true" className="max-h-[72%] w-auto max-w-[94%] object-contain" />
              </div>

              <div className="max-w-[560px] pb-4">
                <p className="text-[clamp(2rem,3vw,3.25rem)] font-medium leading-[1.08] text-white">
                  Intelligent Meal Planning
                </p>
                <p className="mt-3 text-[clamp(1.15rem,1.55vw,2rem)] leading-[1.35] text-white/75">
                  Create personalized, condition-specific meal plans in minutes - not hours.
                </p>
              </div>
            </div>
          </div>
        </aside>

        <main className="flex min-h-0 w-full items-center justify-center overflow-y-auto bg-white p-4 sm:p-6 lg:p-10">
          <div className="w-full max-w-[460px]">
            <Link
              to="/"
              aria-label="CalorieScience home"
              className="mb-6 lg:mb-8 block w-fit rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20AEDC] focus-visible:ring-offset-2"
            >
              <img src={menuBarLogo} alt="Calorie Science" className="h-10 w-auto max-w-[220px]" />
            </Link>

            {step === 'otp' ? (
              <form
                className="space-y-5"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleVerifyOtp();
                }}
              >
                <h1 className="text-4xl font-semibold leading-tight text-slate-800">OTP Verification</h1>
                <p className="text-sm leading-6 text-slate-500">
                  Enter the six-digit code sent to your email
                  {pendingEmail ? ` (${pendingEmail}).` : '.'}
                </p>

                <div className="grid grid-cols-6 gap-2 sm:flex sm:items-center sm:gap-3">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(element) => {
                        otpRefs.current[index] = element;
                      }}
                      value={digit}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="done"
                      maxLength={OTP_LENGTH}
                      pattern="[0-9]*"
                      onChange={(event) => handleOtpChange(index, event.target.value)}
                      onKeyDown={(event) => handleOtpKeyDown(index, event)}
                      onPaste={handleOtpPaste}
                      className="!h-11 !w-full !rounded-md !border-slate-300 !bg-white !p-0 text-lg font-semibold sm:!h-12 sm:!w-12 [&_.MuiOutlinedInput-input]:!p-0 [&_.MuiOutlinedInput-input]:!text-center"
                      aria-label={`OTP digit ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  type="submit"
                  disabled={isVerifyingOtp}
                  className="!h-11 !w-full !rounded-md !bg-[#20AEDC] !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
                >
                  {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                </Button>

                <p className="text-sm font-medium text-slate-600">
                  Didn&apos;t receive email? Check your spam folder, or{' '}
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isResendingOtp || resendCooldownSeconds > 0}
                    className="font-semibold text-[#0f7fa4] underline disabled:opacity-60"
                  >
                    {isResendingOtp
                      ? 'Resending...'
                      : resendCooldownSeconds > 0
                        ? `Resend in ${resendCooldownSeconds}s`
                        : 'Resend'}
                  </button>
                </p>

                <p className="text-center text-sm text-slate-600">
                  <button
                    type="button"
                    className="font-medium text-[#3198c4] hover:underline"
                    onClick={returnToCredentialsStep}
                  >
                    Back to login
                  </button>
                </p>
              </form>
            ) : (
              <>
                <h1 className="text-4xl font-semibold leading-tight text-slate-800">Welcome back!</h1>
                <p className="mt-2 text-sm text-slate-500">
                  Enter your credentials to access your account
                </p>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium uppercase tracking-[0.06em] text-slate-500">
                            Email*
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              autoComplete="email"
                              placeholder="Enter your email"
                              className="!h-12 !rounded-md !border-slate-300 !bg-white text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium uppercase tracking-[0.06em] text-slate-500">
                            Password*
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? "text" : "password"}
                                autoComplete="current-password"
                                placeholder="********"
                                className="!h-12 !rounded-md !border-slate-300 !bg-white pr-10 text-sm"
                                {...field}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end pt-1">
                      <Link to="/forgot-password" className="text-sm font-medium text-slate-700 underline">
                        Forgot your password
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      className="!mt-4 !h-11 !w-full !rounded-md !bg-[#20AEDC] !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
                      disabled={isLoading}
                    >
                      {isLoading ? "Logging in..." : "Login"}
                    </Button>

                    <div className="flex items-center gap-3 pt-2 text-xs uppercase tracking-[0.14em] text-slate-400">
                      <span className="h-px flex-1 bg-slate-200" />
                      <span>or</span>
                      <span className="h-px flex-1 bg-slate-200" />
                    </div>

                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={handleGoogleOAuth}
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white shadow-sm transition-colors hover:bg-slate-50"
                        aria-label="Continue with Google"
                      >
                        <img src={googleOAuthIcon} alt="" aria-hidden="true" className="h-[26px] w-[26px]" />
                      </button>
                    </div>

                    <p className="text-center text-sm text-slate-600">
                      Don&apos;t have an account?{' '}
                      <Link to="/signup" className="font-medium text-[#3198c4] hover:underline">
                        Signup
                      </Link>
                    </p>
                  </form>
                </Form>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
